/** @jsxImportSource @emotion/react */
import React from 'react';
import { Tag } from 'antd';
import { ExperimentOutlined, ToolOutlined } from '@ant-design/icons';
import { EventKind } from '../types/plantingEvents';
import { css } from '@emotion/react';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';

/**
 * Вспомогательные функции для работы с типом события (Event Kind)
 * CN-0008: Автоматическое определение типа события
 */

/**
 * Иконка для типа события
 */
export const getEventKindIcon = (kind: EventKind | string | null | undefined): React.ReactNode => {
  if (!kind) {
    return null;
  }

  switch (kind) {
    case 'measurement':
      return <ExperimentOutlined css={css`color: #1890ff;`} />;
    case 'service':
      return <ToolOutlined css={css`color: #52c41a;`} />;
    default:
      return null;
  }
};

/**
 * Эмодзи иконка для типа события (альтернатива)
 */
export const getEventKindEmoji = (kind: EventKind | string | null | undefined): string => {
  if (!kind) {
    return '📋';
  }

  switch (kind) {
    case 'measurement':
      return '📊'; // или 📏 📐
    case 'service':
      return '🔧'; // или 🛠️ ⚙️
    default:
      return '📋';
  }
};

/**
 * Tag компонент для типа события
 */
export const EventKindTag: React.FC<{ 
  kind: EventKind | string | null | undefined;
  showIcon?: boolean;
}> = ({ kind, showIcon = true }) => {
  const { t } = useTranslation('events');
  if (!kind) {
    return null;
  }

  const config = {
    measurement: {
      color: 'blue',
      label: t('kinds.measurement'),
      icon: <ExperimentOutlined />,
    },
    service: {
      color: 'green',
      label: t('kinds.service'),
      icon: <ToolOutlined />,
    },
  };

  const kindConfig = config[kind as EventKind];
  if (!kindConfig) {
    return null;
  }

  return (
    <Tag color={kindConfig.color} icon={showIcon ? kindConfig.icon : undefined}>
      {kindConfig.label}
    </Tag>
  );
};

/**
 * Получить человекочитаемое название типа события
 */
export const getEventKindLabel = (kind: EventKind | string | null | undefined): string => {
  if (!kind) {
    return i18n.t('events:kinds.unknown');
  }

  switch (kind) {
    case 'measurement':
      return i18n.t('events:kinds.measurement');
    case 'service':
      return i18n.t('events:kinds.service');
    default:
      return i18n.t('events:kinds.unknown');
  }
};

/**
 * Получить описание типа события
 */
export const getEventKindDescription = (kind: EventKind | string | null | undefined): string => {
  if (!kind) {
    return i18n.t('events:kinds.undefinedDescription');
  }

  switch (kind) {
    case 'measurement':
      return i18n.t('events:kinds.measurementDescription');
    case 'service':
      return i18n.t('events:kinds.serviceDescription');
    default:
      return i18n.t('events:kinds.undefinedDescription');
  }
};
